/** 运营后台的账号与权限模型。与用户侧完全隔离：员工不是客户。 */

export type AdminRole = 'super_admin' | 'ops' | 'support' | 'finance' | 'viewer';

export const ADMIN_ROLES: readonly AdminRole[] = ['super_admin', 'ops', 'support', 'finance', 'viewer'];

export type AdminPermission =
  | 'dashboard.view'
  | 'customer.view'
  | 'customer.manage'
  | 'tenant.review'
  | 'product.view'
  | 'product.manage'
  | 'order.view'
  | 'order.refund'
  | 'ticket.view'
  | 'ticket.reply'
  | 'audit.view'
  | 'settings.view'
  | 'settings.manage'
  | 'content.view'
  | 'content.manage'
  | 'staff.manage';

export const ADMIN_PERMISSIONS: readonly AdminPermission[] = [
  'dashboard.view',
  'customer.view',
  'customer.manage',
  'tenant.review',
  'product.view',
  'product.manage',
  'order.view',
  'order.refund',
  'ticket.view',
  'ticket.reply',
  'audit.view',
  'settings.view',
  'settings.manage',
  'content.view',
  'content.manage',
  'staff.manage',
];

/**
 * 角色矩阵 —— 唯一事实来源。
 *
 * 划分依据是「最小必要」：
 *   * support 能看客户但不能改状态、不能碰金额；
 *   * finance 只管钱（订单/退款），不碰账号与产品；
 *   * ops 管产品与审核；
 *   * super_admin 才管员工账号。
 */
export const ROLE_PERMISSIONS: Readonly<Record<AdminRole, readonly AdminPermission[]>> = {
  super_admin: ADMIN_PERMISSIONS,
  ops: [
    'dashboard.view',
    'customer.view',
    'customer.manage',
    'tenant.review',
    'product.view',
    'product.manage',
    'order.view',
    'ticket.view',
    'ticket.reply',
    'audit.view',
    'settings.view',
    'settings.manage',
    'content.view',
    'content.manage',
  ],
  support: [
    'dashboard.view',
    'customer.view',
    'ticket.view',
    'ticket.reply',
    'audit.view',
    'settings.view',
    'content.view',
  ],
  finance: [
    'dashboard.view',
    'customer.view',
    'order.view',
    'order.refund',
    'audit.view',
    'settings.view',
  ],
  viewer: [
    'dashboard.view',
    'customer.view',
    'product.view',
    'order.view',
    'ticket.view',
    'audit.view',
    'settings.view',
    'content.view',
  ],
};

export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  super_admin: '超级管理员',
  ops: '运营',
  support: '客服',
  finance: '财务',
  viewer: '只读',
};

export const PERMISSION_LABELS: Readonly<Record<AdminPermission, string>> = {
  'dashboard.view': '查看概览',
  'customer.view': '查看客户与组织',
  'customer.manage': '停用/启用客户账号',
  'tenant.review': '审核企业资质',
  'product.view': '查看产品',
  'product.manage': '上架/编辑产品',
  'order.view': '查看订单',
  'order.refund': '退款',
  'ticket.view': '查看工单',
  'ticket.reply': '回复工单',
  'audit.view': '查看审计',
  'settings.view': '查看平台开关',
  'settings.manage': '调整平台开关',
  'content.view': '查看内容管理',
  'content.manage': '编辑技术文章与待上新产品',
  'staff.manage': '管理员工账号',
};

export function isAdminRole(value: unknown): value is AdminRole {
  return typeof value === 'string' && (ADMIN_ROLES as readonly string[]).includes(value);
}

export function hasPermission(
  role: AdminRole | null | undefined,
  permission: AdminPermission,
): boolean {
  if (!role) return false;
  return (ROLE_PERMISSIONS[role] ?? []).includes(permission);
}

export function permissionsForRole(role: AdminRole): AdminPermission[] {
  return [...(ROLE_PERMISSIONS[role] ?? [])];
}
